import { showErrorMessage, showSuccessMessage } from 'components/Helpers/Toasts';
import { getUserFriendlyErrors } from 'connectors/lib/friendlyErrorMessageParser';
import { ApplyLoyaltyClubPointsMutationApi, useApplyLoyaltyClubPointsMutationApi } from 'graphql/generated';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useCallback } from 'react';
import { usePersistStore } from 'store/usePersistStore';

type ApplyLoyaltyPointsHandler = (
    loyaltyClubPoints: number,
    messages: { success: string; error: string },
) => Promise<ApplyLoyaltyClubPointsMutationApi['ApplyLoyaltyClubPoints'] | void>;

export const useApplyLoyaltyPointsToCart = (): [ApplyLoyaltyPointsHandler, boolean] => {
    const [{ fetching }, applyLoyaltyPointsToCart] = useApplyLoyaltyClubPointsMutationApi();
    const { cartUuid } = usePersistStore((s) => s);
    const { defaultStoreUuid } = usePersistStore((s) => s);

    const t = useTypedTranslationFunction();

    const applyLoyaltyPointsToCartHandler = useCallback<ApplyLoyaltyPointsHandler>(
        async (loyaltyClubPoints, messages) => {
            const { data, error } = await applyLoyaltyPointsToCart({
                input: { cartUuid, loyaltyClubPoints },
                defaultStoreUuid,
                selectedStoreUuid: null,
            });

            if (error !== undefined) {
                const { userError } = getUserFriendlyErrors(error, t);
                if (userError?.validation?.loyaltyPoints !== undefined) {
                    showErrorMessage(userError.validation.loyaltyPoints.message);
                } else {
                    showErrorMessage(messages.error);
                }

                return undefined;
            }

            showSuccessMessage(messages.success);

            return data?.ApplyLoyaltyClubPoints;
        },
        [applyLoyaltyPointsToCart, cartUuid, t, defaultStoreUuid],
    );

    return [applyLoyaltyPointsToCartHandler, fetching];
};
